import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import commissionService from '../../services/ShipmentCommissionService.js';
import commissionPaymentService from '../../services/ShipmentCommissionPaymentService.js';
import AddPaymentModal from './commission_payment/AddPaymentModal.js';
import EditPaymentModal from './commission_payment/EditPaymentModal.js';
import ViewPaymentModal from './commission_payment/ViewPaymentModal.js';
import DocumentModal from '../common/DocumentModal.js';
import { DollarSign, Pencil, ArrowLeft, Plus, Eye, ChevronDown, ChevronRight } from 'lucide-react';
import {
  PageLayout,
  Button,
  DetailCard,
  DetailRow,
  StatusBadge,
} from '../common/index.js';

const ViewCommission = () => {
  const { commissionId } = useParams();
  const [commission, setCommission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false);
  const [isEditPaymentOpen, setIsEditPaymentOpen] = useState(false);
  const [isViewPaymentOpen, setIsViewPaymentOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isPaymentDocModalOpen, setIsPaymentDocModalOpen] = useState(false);
  const [selectedPaymentAttachment, setSelectedPaymentAttachment] = useState(null);
  const [expandedHubs, setExpandedHubs] = useState({});
  const [hubPaymentData, setHubPaymentData] = useState({ hub_id: '', hub_type: '', commission_id: '' });
  const navigate = useNavigate();

  const fetchCommission = useCallback(async () => {
    try {
      const response = await commissionService.getCommission(commissionId);
      setCommission(response.data.data);
    } catch (err) {
      Alert.error('Failed to load commission');
      console.error(err);
      navigate('/commissions');
    } finally {
      setLoading(false);
    }
  }, [commissionId, navigate]);

  useEffect(() => {
    fetchCommission();
  }, [fetchCommission]);

  const handleDeletePayment = async (paymentId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to delete this payment? This will update the commission balance.',
      'Delete Payment'
    );
    if (confirmed) {
      try {
        await commissionPaymentService.deletePayment(paymentId);
        Alert.success('Payment deleted successfully!');
        fetchCommission();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete payment');
      }
    }
  };

  const handleEditPayment = (payment) => {
    setSelectedPayment(payment);
    setIsEditPaymentOpen(true);
  };

  const handlePaymentSuccess = () => {
    setIsAddPaymentOpen(false);
    setIsEditPaymentOpen(false);
    setSelectedPayment(null);
    setHubPaymentData({ hub_id: '', hub_type: '', commission_id: '' });
    fetchCommission();
  };

  const handleHubPay = (hub, hubType) => {
    setHubPaymentData({ hub_id: hub.hub_id, hub_type: hubType, commission_id: commissionId });
    setIsAddPaymentOpen(true);
  };

  const toggleHubExpand = (key) => {
    setExpandedHubs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getRecipientTypeColor = (type) => {
    const map = {
      COMPANY:       'bg-green-100 text-green-700',
      COMPANY_OWNER: 'bg-blue-100 text-brand',
      VEHICLE_OWNER: 'bg-blue-100 text-brand',
      SENDER_HUB:    'bg-purple-100 text-purple-700',
      RECEIVER_HUB:  'bg-indigo-100 text-indigo-700',
    };
    return map[type] || 'bg-gray-100 text-gray-700';
  };

  const formatRecipientType = (type) => {
    const map = {
      COMPANY: 'Company',
      COMPANY_OWNER: 'Vehicle Owner',
      VEHICLE_OWNER: 'Vehicle Owner',
      SENDER_HUB: 'Sender Hub',
      RECEIVER_HUB: 'Receiver Hub',
    };
    return map[type] || type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderHubCommissionsSection = (hubs, title, hubType) => {
    if (!hubs || hubs.length === 0) return null;

    const totalCommission = hubs.reduce((s, h) => s + parseFloat(h.hub_commission_amount || 0), 0);
    const totalPaid = hubs.reduce((s, h) => s + parseFloat(h.paid_amount || 0), 0);

    return (
      <div>
        <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
          {title}
        </h3>
        <div className="overflow-x-auto mb-3">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 w-8"></th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">HUB LOCATION</th>
                <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700">HUB %</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">COMMISSION</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">FREIGHT</th>
                <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700">SHIPMENTS</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">PAID</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">BALANCE</th>
                <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700">STATUS</th>
                <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700">ACTION</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {hubs.map((hub) => {
                const key = `${hubType}-${hub.hub_id}`;
                const isExpanded = expandedHubs[key];
                const balance = parseFloat(hub.hub_commission_amount || 0) - parseFloat(hub.paid_amount || 0);
                return (
                  <React.Fragment key={hub.hub_id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 cursor-pointer" onClick={() => toggleHubExpand(key)}>
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-secondary">{hub.hub_location} ({hub.hub_id})</td>
                      <td className="px-4 py-3 text-sm text-center">{parseFloat(hub.hub_commission_percent || 0).toFixed(2)}%</td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-secondary">₹{parseFloat(hub.hub_commission_amount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-sm text-right">₹{parseFloat(hub.hub_total_freight || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-sm text-center">{hub.hub_total_shipments || 0}</td>
                      <td className="px-4 py-3 text-sm text-right text-green-600 font-semibold">₹{parseFloat(hub.paid_amount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-sm text-right text-red-500 font-semibold">₹{balance.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge status={hub.payment_status || 'PENDING'} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        {hub.payment_status !== 'PAID' && (
                          <Button size="sm" onClick={() => handleHubPay(hub, hubType)}>
                            Pay
                          </Button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && hub.agents && hub.agents.length > 0 && (
                      <tr>
                        <td colSpan="10" className="px-4 py-0">
                          <div className="bg-brand-light border border-blue-100 rounded-sm my-2 mx-4">
                            <table className="w-full text-xs">
                              <thead className="bg-blue-100">
                                <tr>
                                  <th className="px-3 py-1.5 text-left font-semibold text-blue-800">{hubType === 'sender' ? 'Agent Name' : 'User Name'}</th>
                                  <th className="px-3 py-1.5 text-left font-semibold text-blue-800">City</th>
                                  {hubType === 'sender' && <th className="px-3 py-1.5 text-center font-semibold text-blue-800">Shipments</th>}
                                  {hubType === 'sender' && <th className="px-3 py-1.5 text-right font-semibold text-blue-800">Freight</th>}
                                  <th className="px-3 py-1.5 text-center font-semibold text-blue-800">Comm. %</th>
                                  <th className="px-3 py-1.5 text-right font-semibold text-blue-800">Comm. Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {hub.agents.map((agent) => (
                                  <tr key={agent.agent_id} className="border-t border-blue-200">
                                    <td className="px-3 py-1.5">{agent.agent_name} ({agent.agent_id})</td>
                                    <td className="px-3 py-1.5 text-gray-600">{agent.agent_city || '-'}</td>
                                    {hubType === 'sender' && <td className="px-3 py-1.5 text-center">{agent.booked_shipments}</td>}
                                    {hubType === 'sender' && <td className="px-3 py-1.5 text-right">₹{parseFloat(agent.total_freight || 0).toLocaleString('en-IN')}</td>}
                                    <td className="px-3 py-1.5 text-center">{parseFloat(agent.agent_commission_percent || 0).toFixed(1)}%</td>
                                    <td className="px-3 py-1.5 text-right font-semibold">₹{parseFloat(agent.agent_commission_amount || 0).toLocaleString('en-IN')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-100 border-t-2 border-gray-300">
              <tr>
                <td colSpan="3" className="px-4 py-2 text-xs font-bold text-right text-gray-700">TOTALS</td>
                <td className="px-4 py-2 text-sm font-bold text-right text-secondary">₹{totalCommission.toLocaleString('en-IN')}</td>
                <td colSpan="2"></td>
                <td className="px-4 py-2 text-sm font-bold text-right text-green-600">₹{totalPaid.toLocaleString('en-IN')}</td>
                <td className="px-4 py-2 text-sm font-bold text-right text-red-500">₹{(totalCommission - totalPaid).toLocaleString('en-IN')}</td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <PageLayout title="Commission Details" icon={DollarSign}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading commission...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!commission) return null;

  return (
    <>
      <PageLayout
        title="Commission Details"
        icon={DollarSign}
        actions={
          <>
            <Button icon={Plus} onClick={() => setIsAddPaymentOpen(true)}>
              Add Payment
            </Button>
            <Button variant="secondary" icon={Pencil} onClick={() => navigate(`/commissions/edit/${commissionId}`)}>
              Edit
            </Button>
            <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/commissions')}>
              Back
            </Button>
          </>
        }
      >
        <div className="bg-primary rounded-card p-4 mb-4">
          <h2 className="text-xl font-semibold text-secondary">{commission.commission_id}</h2>
          <p className="text-xs text-gray-500">ML Sheet: {commission.ml_sheet_id}</p>
        </div>

        <div className="space-y-4">
          <DetailCard title="Basic Information">
            <DetailRow label="Commission ID" value={commission.commission_id} />
            <DetailRow label="ML Sheet ID" value={commission.ml_sheet_id} />
            <DetailRow label="Total Freight" value={`₹${parseFloat(commission.total_freight || 0).toLocaleString('en-IN')}`} />
            <DetailRow label="Created" value={`${formatDate(commission.created_at)} by ${commission.created_by}`} />
          </DetailCard>

          <DetailCard title="Freight Breakdown">
            <DetailRow label="To Pay Freight" value={`₹${parseFloat(commission.to_pay_freight || 0).toLocaleString('en-IN')}`} />
            <DetailRow label="Paid Freight" value={`₹${parseFloat(commission.paid_freight || 0).toLocaleString('en-IN')}`} />
            <DetailRow label="Total Recovery" value={`₹${parseFloat(commission.total_recovery || 0).toLocaleString('en-IN')}`} />
          </DetailCard>

          <DetailCard title="Company Owner Commission">
            <DetailRow label="Owner ID" value={commission.vehicle_owner_id} />
            <DetailRow label="Commission %" value={`${parseFloat(commission.vehicle_owner_commission_percent || 0).toFixed(2)}%`} />
            <DetailRow label="Commission Amount" value={`₹${parseFloat(commission.vehicle_owner_commission_amount || 0).toLocaleString('en-IN')}`} />
            <DetailRow
              label="Paid / Balance"
              value={
                <>
                  <span className="text-green-600">₹{parseFloat(commission.vehicle_owner_paid_amount || 0).toLocaleString('en-IN')}</span>
                  {' / '}
                  <span className="text-red-500">₹{(parseFloat(commission.vehicle_owner_commission_amount || 0) - parseFloat(commission.vehicle_owner_paid_amount || 0)).toLocaleString('en-IN')}</span>
                </>
              }
            />
            <DetailRow label="Status" value={<StatusBadge status={commission.vehicle_owner_payment_status} />} />
          </DetailCard>

          <DetailCard title="Company Commission">
            <DetailRow label="Company ID" value={commission.company_profile_id} />
            <DetailRow label="Commission %" value={`${parseFloat(commission.company_commission_percent || 0).toFixed(2)}%`} />
            <DetailRow label="Commission Amount" value={`₹${parseFloat(commission.company_commission_amount || 0).toLocaleString('en-IN')}`} />
            <DetailRow
              label="Paid / Balance"
              value={
                <>
                  <span className="text-green-600">₹{parseFloat(commission.company_paid_amount || 0).toLocaleString('en-IN')}</span>
                  {' / '}
                  <span className="text-red-500">₹{(parseFloat(commission.company_commission_amount || 0) - parseFloat(commission.company_paid_amount || 0)).toLocaleString('en-IN')}</span>
                </>
              }
            />
            <DetailRow label="Status" value={<StatusBadge status={commission.company_payment_status} />} />
          </DetailCard>

          {renderHubCommissionsSection(commission.sender_hub_commissions, 'Sender Hub Commissions', 'sender')}
          {renderHubCommissionsSection(commission.receiver_hub_commissions, 'Receiver Hub Commissions', 'receiver')}

          <DetailCard title="Payment History">
                      {commission.payments && commission.payments.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">PAYMENT ID</th>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">RECIPIENT</th>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">TYPE</th>
                                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">AMOUNT</th>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">DATE</th>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">MODE</th>
                                <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700">ACTIONS</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {commission.payments.map((payment) => (
                                <tr key={payment.payment_id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-xs text-gray-500">{payment.payment_id}</td>
                                  <td className="px-4 py-3 text-sm">{payment.recipient_name}</td>
                                  <td className="px-4 py-3">
                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getRecipientTypeColor(payment.recipient_type)}`}>
                                      {formatRecipientType(payment.recipient_type)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-right font-semibold">₹{parseFloat(payment.amount || 0).toLocaleString('en-IN')}</td>
                                  <td className="px-4 py-3 text-sm">{formatDate(payment.payment_date)}</td>
                                  <td className="px-4 py-3 text-sm">{payment.payment_mode || 'N/A'}</td>
                                  <td className="px-4 py-3 text-center">
                                    <div className="flex gap-2 justify-center">
                                      <button
                                        onClick={() => { setSelectedPayment(payment); setIsViewPaymentOpen(true); }}
                                        className="text-brand hover:text-blue-800"
                                        title="View Payment"
                                      >
                                        <Eye className="w-4 h-4" />
                                      </button>
                                      <button onClick={() => handleEditPayment(payment)} className="text-blue-600 hover:text-blue-800 text-xs" title="Edit">✏️</button>
                                      <button onClick={() => handleDeletePayment(payment.payment_id)} className="text-red-600 hover:text-red-800 text-xs" title="Delete">🗑️</button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4 text-sm">No payments recorded yet</p>
                      )}
          </DetailCard>
        </div>
      </PageLayout>

      {isAddPaymentOpen && (
        <AddPaymentModal
          commissionId={commissionId}
          commission={commission}
          hubPaymentData={hubPaymentData}
          onClose={() => { setIsAddPaymentOpen(false); setHubPaymentData({ hub_id: '', hub_type: '', commission_id: '' }); }}
          onSuccess={handlePaymentSuccess}
        />
      )}
      {isViewPaymentOpen && selectedPayment && (
        <ViewPaymentModal payment={selectedPayment} onClose={() => { setIsViewPaymentOpen(false); setSelectedPayment(null); }} />
      )}
      {isEditPaymentOpen && selectedPayment && (
        <EditPaymentModal payment={selectedPayment} commission={commission} onClose={() => { setIsEditPaymentOpen(false); setSelectedPayment(null); }} onSuccess={handlePaymentSuccess} />
      )}
      <DocumentModal
        isOpen={isPaymentDocModalOpen}
        onClose={() => { setIsPaymentDocModalOpen(false); setSelectedPaymentAttachment(null); }}
        documentUrl={selectedPaymentAttachment?.url}
        documentType={selectedPaymentAttachment?.fileName?.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image'}
      />
    </>
  );
};

export default ViewCommission;
